import { theme } from '@Guidelines/Theme/theme';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import ArchivedFieldRow from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/Sections/Sheet/ArchivedFieldRow';
import type SubjectTypeDefinitionFormData from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/FormData/SubjectTypeDefinitionFormData';
import { css } from '@emotion/react';
import * as React from 'react';
import { useSelector } from '@verclam/synapse';

const summaryStyle = css`
	cursor: pointer;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	font-weight: ${theme.fontWeights.semibold};
	color: ${theme.colors.ink};
`;

const listStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.625rem;
	margin-top: 0.75rem;
`;

type Props = {
	readonly formData: SubjectTypeDefinitionFormData;
	readonly onChange: (
		update: (formData: SubjectTypeDefinitionFormData) => SubjectTypeDefinitionFormData,
	) => void;
	readonly isReadOnly: boolean;
};

const ARCHIVED_TITLES: Readonly<Record<'one' | 'many', (count: number) => string>> = {
	one: () => 'Champs archivés (1)',
	many: (count) => `Champs archivés (${count})`,
};

/** The archived fields, collapsed; each one can be restored with its values and place in the list. */
const ArchivedFields: React.FC<Props> = ({ formData, onChange, isReadOnly }) => {
	const natures = useSelector((state) => state.subjectTypeDefinitionsStore.fieldNatures);

	const archived = React.useMemo(
		() =>
			formData.fields.map((field, index) => ({ field, index })).filter((entry) => entry.field.archived),
		[formData.fields],
	);

	const handleRestore = React.useCallback(
		(index: number) => {
			onChange((current) => current.restoreField(index));
		},
		[onChange],
	);

	return (
		<ConditionalLoader data={archived.length > 0}>
			<details>
				<summary css={summaryStyle}>
					{ARCHIVED_TITLES[archived.length === 1 ? 'one' : 'many'](archived.length)}
				</summary>
				<div css={listStyle}>
					{archived.map(({ field, index }) => (
						<ArchivedFieldRow
							key={field.key ?? `archived-${index}`}
							index={index}
							label={field.label}
							natureLabel={
								natures?.find((nature) => nature.value === field.nature)?.label ??
								field.natureLabel
							}
							isReadOnly={isReadOnly}
							onRestore={handleRestore}
						/>
					))}
				</div>
			</details>
		</ConditionalLoader>
	);
};

export default ArchivedFields;
